import { useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import {
  useGetTraderListCollapseQuery,
  useSetCollapseAllMutation,
  useSetTraderCollapsedMutation,
} from 'store/services/settings/traderListCollapseApi';
import {
  DEFAULT_TRADER_LIST_COLLAPSE_PREFS,
  TraderListCollapsePrefs,
  isTraderCollapsed,
} from 'utils/traderListCollapse';

export interface TraderListCollapseApi {
  prefs: TraderListCollapsePrefs;
  collapseAll: boolean;
  isCollapsed: (traderId: number | string) => boolean;
  setTraderCollapsed: (traderId: number | string, collapsed: boolean) => void;
  /** Flip collapse-all; the server clears every per-trader override with it. */
  toggleCollapseAll: () => void;
}

/**
 * One account-level collapse setting for both Send Cards and Activity. Fetched
 * once (RTK cache) and reused; writes are optimistic and roll back with a toast.
 */
export const useTraderListCollapse = (): TraderListCollapseApi => {
  const { data } = useGetTraderListCollapseQuery();
  const [putAll] = useSetCollapseAllMutation();
  const [putTrader] = useSetTraderCollapsedMutation();
  const prefs = data ?? DEFAULT_TRADER_LIST_COLLAPSE_PREFS;

  const isCollapsed = useCallback(
    (traderId: number | string) => isTraderCollapsed(prefs, traderId),
    [prefs],
  );

  const setTraderCollapsed = useCallback(
    (traderId: number | string, collapsed: boolean) => {
      putTrader({ traderUserId: traderId, collapsed })
        .unwrap()
        .catch(() => toast.error('Failed to save. Please try again.'));
    },
    [putTrader],
  );

  const toggleCollapseAll = useCallback(() => {
    putAll({ collapseAll: !prefs.collapseAll })
      .unwrap()
      .catch(() => toast.error('Failed to save. Please try again.'));
  }, [putAll, prefs.collapseAll]);

  return useMemo(
    () => ({ prefs, collapseAll: prefs.collapseAll, isCollapsed, setTraderCollapsed, toggleCollapseAll }),
    [prefs, isCollapsed, setTraderCollapsed, toggleCollapseAll],
  );
};

export default useTraderListCollapse;
